import Entry from "./Entry.js";

/**
 * `folder` is a concrete implementation of `entry` representing a
 * folder.
 *
 * @param {boolean} spec.isRoot - Indicate if the folder is at the top of the entry hierarchy
 * @param {entry[]} [spec.children=[]] - Array of entries in this folder
 */
export default class Folder extends Entry {
	constructor({ isRoot, children = [] } = {}) {
		super(...arguments);
		this._isRoot = isRoot;
		this._children = children;

		this._children.forEach((each) => {
			each.setParent(this);
		});
	}

	addChild(entry) {
		if (this._children.indexOf(entry) !== -1) {
			return;
		}
		entry.setParent(this);
		this._children.push(entry);
	}

	addChildren(entries) {
		entries.forEach((entry) => {
			this.addChild(entry);
		});
	}

	removeChildren(entries) {
		// Possible optimisation here
		entries.forEach((entry) => {
			const index = this._children.indexOf(entry);
			if (index !== -1) {
				this._children.splice(index, 1);
			}
		});
	}

	hasChildren() {
		return this._children.length > 0;
	}

	children() {
		return this._children;
	}

	hasChildWithName(name) {
		return this.children().some((child) => child.name() === name);
	}

	isRoot() {
		return this._isRoot;
	}

	size() {
		let result = 0;

		this.children().forEach((each) => {
			result += each.size();
		});

		return result;
	}

	type() {
		return "Folder";
	}

	isFolder() {
		return true;
	}

	numberOfSubDirectories() {
		let result = 0;

		this.children().forEach((each) => {
			if (!each.isFolder()) {
				return;
			}
			result = result + 1;
		});

		return result;
	}

	numberOfDocuments() {
		let result = 0;

		this.children().forEach((each) => {
			if (!each.isFile()) {
				return;
			}
			result = result + 1;
		});

		return result;
	}

	_buildWidgetFor(aView, position) {
		return aView.buildFolderWidgetFor(this, position);
	}

	_serializedProperties() {
		const data = super._serializedProperties();

		data.isRoot = this._isRoot;

		return data;
	}

	static transformMaterializationData(data, context) {
		data.children = context.factory.materializeAll(data.children, context);

		super.transformMaterializationData(data);
	}
}
